import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { HubPage, Container, Band, SectionHead, Seo } from "../components/hub/HubKit";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "mt-1.5 w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-sm text-ink outline-none focus:border-sage";

export default function ShareStory() {
  const [f, setF] = useState({ name: "", email: "", title: "", story: "", photo: "", support_url: "", display_pref: "first", consent: false });
  const [state, setState] = useState({ sending: false, done: false, err: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) { setState({ ...state, err: "Please confirm you have permission to share this story." }); return; }
    setState({ sending: true, done: false, err: "" });
    try {
      const res = await fetch(`${API}/api/stories/submit`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(f) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Something went wrong.");
      setState({ sending: false, done: true, err: "" });
    } catch (err) { setState({ sending: false, done: false, err: err.message }); }
  };

  return (
    <HubPage testid="share-story-page">
      <Seo title="Share Your Story with HEARD" description="Share a real-life story with the HEARD community. Submissions are reviewed before anything is published." />
      <Band tone="sage" className="border-b border-line/60">
        <Container className="pt-12 pb-10 sm:pt-16">
          <div className="mx-auto max-w-2xl">
            <SectionHead overline="Community Stories" title="Share your story with HEARD" sub="Real experiences of support, resilience, family, joy, and showing up for one another. Nothing is published without review — you're always in control of how you appear." />
          </div>
        </Container>
      </Band>

      <Container className="py-12 sm:py-16">
        <div className="mx-auto max-w-2xl">
          {state.done ? (
            <div className="rounded-3xl border border-line bg-white/80 p-8 text-center" data-testid="share-success">
              <CheckCircle2 className="mx-auto text-sage-deep" size={40} />
              <h2 className="serif-display mt-4 text-2xl text-charcoal">Thank you for sharing.</h2>
              <p className="mt-3 text-ink/70">Your story has been sent to the HEARD team for a gentle review. We'll be in touch if we publish it.</p>
              <Link to="/community" className="mt-6 inline-block rounded-full bg-sage-deep px-6 py-3 text-sm text-cream" data-testid="share-back-community">Back to the Community</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-line bg-white/80 p-6 sm:p-8" data-testid="share-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm text-ink/70">Your name<input value={f.name} onChange={set("name")} required className={inputCls} data-testid="share-name" /></label>
                <label className="block text-sm text-ink/70">Email<input type="email" value={f.email} onChange={set("email")} required className={inputCls} data-testid="share-email" /></label>
              </div>
              <label className="mt-4 block text-sm text-ink/70">Story title<input value={f.title} onChange={set("title")} required className={inputCls} data-testid="share-title" /></label>
              <label className="mt-4 block text-sm text-ink/70">Your story<textarea rows={7} value={f.story} onChange={set("story")} required className={inputCls} data-testid="share-story" /></label>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="block text-sm text-ink/70">Photo link <span className="text-ink/40">(optional)</span><input value={f.photo} onChange={set("photo")} placeholder="https://…" className={inputCls} data-testid="share-photo" /></label>
                <label className="block text-sm text-ink/70">Support link <span className="text-ink/40">(optional)</span><input value={f.support_url} onChange={set("support_url")} placeholder="e.g. a GoFundMe URL" className={inputCls} data-testid="share-support" /></label>
              </div>
              <label className="mt-4 block text-sm text-ink/70">How would you like to appear?
                <select value={f.display_pref} onChange={set("display_pref")} className={inputCls} data-testid="share-display-pref">
                  <option value="full">Full name</option>
                  <option value="first">First name only</option>
                  <option value="anonymous">Anonymous</option>
                </select>
              </label>
              <label className="mt-5 flex items-start gap-3 text-sm text-ink/70" data-testid="share-consent-label">
                <input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-1" data-testid="share-consent" />
                <span>I confirm this is my story (or I have permission to share it), and I'm giving HEARD permission to review and potentially publish it as a Community Story.</span>
              </label>
              {state.err && <p className="mt-4 text-sm text-red-600" data-testid="share-error">{state.err}</p>}
              <button type="submit" disabled={state.sending} className="mt-6 w-full rounded-full bg-sage-deep px-6 py-3.5 text-sm font-medium text-cream disabled:opacity-50" data-testid="share-submit">
                {state.sending ? "Sending…" : "Share my story"}
              </button>
              <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-ink/50"><ShieldCheck size={13} /> Reviewed before anything is published. Your email stays private.</p>
            </form>
          )}
        </div>
      </Container>
    </HubPage>
  );
}
